import { useState } from "react";
import { Undo2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtAmount } from "@/lib/render";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const RefundButton = ({ rec, onDone }) => {
  const left = Math.round((rec.amount - (rec.refunded_amount || 0)) * 100) / 100;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(left));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processor-engine/records/${rec.id}/refund`, { amount: parseFloat(amount), reason });
      toast.success(`Refunded ${fmtAmount(data.refund.amount, "USD")} · ${data.original.status.replace("_", " ")}`);
      setOpen(false); onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="sm" data-testid="pe-refund-button" onClick={() => { setAmount(String(left)); setReason(""); setOpen(true); }}
        className="h-7 text-xs text-amber-500 hover:text-amber-400 hover:bg-amber-500/10">
        <Undo2 className="h-3 w-3 mr-1" />Refund
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-sm" data-testid="pe-refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Refund {rec.kind === "charge" ? "card charge" : "card deposit"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {rec.instrument} · refundable <span data-testid="pe-refund-left" className="font-mono text-foreground">{fmtAmount(left, "USD")}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Amount (USD)</Label>
              <Input data-testid="pe-refund-amount" type="number" step="0.01" min="0.01" max={left} value={amount}
                onChange={(e) => setAmount(e.target.value)} className="bg-muted/40 border-border font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Reason</Label>
              <Input data-testid="pe-refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-muted/40 border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pe-refund-submit" onClick={submit} disabled={busy || !(parseFloat(amount) > 0)}
              className="bg-amber-500 hover:bg-amber-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refund to card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
